import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { environment } from "@environments/environment";
import {
  catchError,
  EMPTY,
  exhaustMap,
  first,
  map,
  Observable,
  startWith,
  switchMap,
  tap,
  throwError,
  timer,
} from "rxjs";
import { attachHttpStatus } from "@shared/utils/api-status.utils";
import {
  EnrichmentImportReport,
  EnrichmentImportResponse,
  PackageExportFormat,
  PackageImportReport,
  PackageImportTask,
} from "../models/enrichment-import-export.model";

/** Client-side mirror of the BFF's `importMaxExportPOIDs` cap
 *  (`GET /api/v1/enrichment/export?poIds=...`, max 100 ids) — enforced here so
 *  an oversized selection fails fast with a clear message instead of round-
 *  tripping to a 400. */
export const ENRICHMENT_EXPORT_MAX_PO_IDS = 100;

/** The sale-channel name the package endpoints expect in `x-channel` from an
 *  AnyChannel caller. The picker's key spelling ("OnlineStore") is not sent. */
export const ONLINE_STORE_X_CHANNEL = "Online Store";

/** How often a running package import is polled. A large workbook runs for
 *  minutes, so there is no overall timeout. */
export const PACKAGE_IMPORT_POLL_MS = 2000;

/** Consecutive failed polls before the import is reported lost. One blip
 *  must not read as a failure while the import keeps writing. */
const PACKAGE_IMPORT_MAX_POLL_FAILURES = 3;

/** The background import settled as `failed`; the message is the task's
 *  `error`. */
export class PackageImportFailedError extends Error {
  override readonly name = "PackageImportFailedError";
}

/** Polling gave up after the upload was accepted. The import may still be
 *  running, and packages it already saved stay saved. */
export class PackageImportLostError extends Error {
  override readonly name = "PackageImportLostError";
}

export type EnrichmentImportHttpResponse = EnrichmentImportResponse & {
  httpStatus: number;
};

/**
 * Enrichment bulk import/export — the per-feature successor to the retired
 * generic excel-template engine (split-engine removal plan, 2026-07-30):
 *   POST /api/v1/enrichment/import  (multipart `file` + `dryRun`)
 *   GET  /api/v1/enrichment/export?poIds=...
 * Gated by `enrichment:import` / `enrichment:export` respectively.
 */
@Injectable({
  providedIn: "root",
})
export class EnrichmentImportExportService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.domain.enricher}`;

  /**
   * Uploads one v1 enrichment import workbook (.xlsx). `dryRun=true`
   * validates everything and writes nothing. The backend answers 400 (not an
   * HttpClient error swallow — it's a real error response) whenever any group
   * fails; the full {@link EnrichmentImportReport} still rides in
   * `error.error.data` on that path, so callers must inspect
   * `HttpErrorResponse.error` on a 400 rather than treating it as a
   * transport failure. See {@link extractEnrichmentImportReport}.
   */
  importWorkbook(
    file: File,
    dryRun: boolean,
    xChannel?: string | null,
  ): Observable<EnrichmentImportHttpResponse> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("dryRun", dryRun ? "true" : "false");

    return this.http
      .post<EnrichmentImportResponse>(
        `${this.url}/enrichment/import`,
        formData,
        {
          headers: xChannel
            ? new HttpHeaders({ "x-channel": xChannel })
            : undefined,
          observe: "response",
        },
      )
      .pipe(
        map((res) => {
          const body = res.body ?? {
            code: "",
            desc: "",
            data: null as unknown as EnrichmentImportReport,
          };
          return attachHttpStatus(body, res.status);
        }),
      );
  }

  /** Downloads the prefilled v1 import workbook for the given PO ids (≤100,
   *  enforced client-side by the caller against
   *  {@link ENRICHMENT_EXPORT_MAX_PO_IDS}). */
  exportWorkbook(
    poIds: readonly string[],
    xChannel?: string | null,
  ): Observable<Blob> {
    const ids = poIds.filter((id) => id && id !== "-").join(",");

    return this.http.get(`${this.url}/enrichment/export`, {
      params: new HttpParams().set("poIds", ids),
      responseType: "blob",
      headers: xChannel
        ? new HttpHeaders({ "x-channel": xChannel })
        : undefined,
    });
  }

  /**
   * Uploads an Online Store package workbook (Akeneo or legacy layout), polls
   * the background task every {@link PACKAGE_IMPORT_POLL_MS}, and emits the
   * final report. Row problems ride in the report. Errors: an upload refusal
   * (400/403 from the POST), {@link PackageImportFailedError}, or
   * {@link PackageImportLostError}. Unsubscribing stops the polling.
   */
  importPackages(
    file: File,
    dryRun: boolean,
    xChannel: string | null,
  ): Observable<PackageImportReport> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("dryRun", dryRun ? "true" : "false");

    return this.http
      .post<{ data: PackageImportTask }>(
        `${this.url}/enrichment-package/import`,
        formData,
        { headers: channelHeaders(xChannel) },
      )
      .pipe(switchMap((res) => this.settlePackageImport(res.data)));
  }

  private settlePackageImport(
    accepted: PackageImportTask,
  ): Observable<PackageImportReport> {
    const taskUrl = `${this.url}/enrichment-package/import/${encodeURIComponent(accepted.id)}`;
    let failedPolls = 0;
    return timer(PACKAGE_IMPORT_POLL_MS, PACKAGE_IMPORT_POLL_MS).pipe(
      exhaustMap(() =>
        this.http
          .get<{ data: PackageImportTask }>(taskUrl)
          .pipe(
            catchError((error: unknown) =>
              ++failedPolls >= PACKAGE_IMPORT_MAX_POLL_FAILURES
                ? throwError(
                    () =>
                      new PackageImportLostError(
                        `lost track of import task ${accepted.id}`,
                        { cause: error },
                      ),
                  )
                : EMPTY,
            ),
          ),
      ),
      tap(() => (failedPolls = 0)),
      map((res) => res.data),
      startWith(accepted),
      first((task) => task.status === "completed" || task.status === "failed"),
      map((task) => {
        if (task.status === "completed" && task.result) return task.result;
        throw new PackageImportFailedError(
          task.error || "The import stopped without a report.",
        );
      }),
    );
  }

  /** Downloads the Online Store package workbook for the given PO ids (each
   *  once, at most {@link ENRICHMENT_EXPORT_MAX_PO_IDS}). */
  exportPackages(
    poIds: readonly string[],
    format: PackageExportFormat,
    xChannel: string | null,
  ): Observable<Blob> {
    const id = [...new Set(poIds.filter((poId) => poId && poId !== "-"))];
    const params =
      format === "legacy"
        ? new HttpParams().set("format", "legacy")
        : undefined;

    return this.http.post(
      `${this.url}/enrichment-package/export`,
      { id },
      { params, headers: channelHeaders(xChannel), responseType: "blob" },
    );
  }
}

function channelHeaders(xChannel: string | null): HttpHeaders | undefined {
  return xChannel ? new HttpHeaders({ "x-channel": xChannel }) : undefined;
}

/** Pulls the {@link EnrichmentImportReport} out of a failed import call's
 *  400 body (`{ data: EnrichmentImportReport }`), mirroring how the easy-app
 *  package import reads its own failure envelope. Returns null for anything
 *  that isn't the expected shape (network error, 401/403, etc). */
export function extractEnrichmentImportReport(
  error: unknown,
): EnrichmentImportReport | null {
  if (!(error instanceof HttpErrorResponse) || error.status !== 400) {
    return null;
  }
  const body: EnrichmentImportResponse | null = error.error ?? null;
  return body?.data ?? null;
}

/** The reason a package import was refused: the upload's 400 `desc`, or a
 *  failed task's `error`, without the BFF's "bad request:" prefix. Null for
 *  anything else. */
export function extractPackageImportRefusal(error: unknown): string | null {
  let reason: unknown = null;
  if (error instanceof PackageImportFailedError) {
    reason = error.message;
  } else if (error instanceof HttpErrorResponse && error.status === 400) {
    reason = error.error?.desc;
  }
  if (typeof reason !== "string") return null;
  const text = reason.replace(/^bad request:\s*/i, "").trim();
  return text === "" ? null : text;
}
